import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useReducedMotion, animate } from 'framer-motion';
import { Home, KeyRound, CalendarRange, LayoutGrid, CircleUser, MoreHorizontal } from 'lucide-react';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { platform } from '@shared/lib/platform';
import { cn } from '@shared/lib/cn';
import type { CompiledNavigationItem } from '@shared/types/domainConfig';
import { PanicSosBadge } from '@features/panic/PanicSosBadge';
import { resolveActiveTabs, routeFor, labelFor, isPanicTab, type TabItem } from './tabBarItems';

const MAX_TABS = 5;
/** Pixels of finger travel before a press becomes a drag rather than a tap. */
const DRAG_THRESHOLD = 8;
const SETTLE_SPRING = { type: 'spring' as const, stiffness: 520, damping: 40, mass: 0.7 };
/** Slide the whole bar off the bottom when hidden. Matches the header's collapse spring. */
const HIDE_SPRING = { type: 'spring' as const, stiffness: 420, damping: 40 };

/**
 * Bottom tab bar, iOS 26 "Liquid Glass" styled: a floating, inset, translucent
 * capsule that reacts to the content behind it. The active tab is a glass lens
 * you can also grab and drag: press and slide across the bar and the lens hops
 * from tab to tab under your finger (with a haptic tick as it lands), settling
 * on whichever tab you release over. Tapping and keyboard focus work as normal.
 *
 * The lens always sits squarely on a tab, because the lit tab renders its icon
 * in white and only the lens gives that white anything to read against.
 *
 * Tabs come from `experienceStore.bottom_tabs` (data-driven per tenant); falls
 * back to Home + Quick Links so the shell still renders before config loads.
 */

const FALLBACK_TABS: Array<{ system_key: string; label: string; route: string; icon: typeof Home }> = [
  { system_key: 'home', label: 'Home', route: '/app/home', icon: Home },
  { system_key: 'estate', label: 'Quick Links', route: '/app/more', icon: LayoutGrid },
];

export function TabBar({ hidden = false }: { hidden?: boolean }): JSX.Element {
  const tabs = useExperienceStore((s) => s.getBottomTabs());
  const experience = useExperienceStore((s) => s.experience);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();

  // Permanent tabs (tenant config, or the fallbacks before it loads), plus a temporary module
  // tab appended on the right when the current route belongs to an unpinned module.
  const baseItems: TabItem[] = (tabs.length > 0 ? tabs : FALLBACK_TABS).slice(0, MAX_TABS);
  const { items, activeIndex } = resolveActiveTabs(baseItems, experience, pathname);

  const listRef = useRef<HTMLUListElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const lensLeft = useMotionValue(0);
  const lensWidth = useMotionValue(48);

  // Drag lives in a ref (stable across renders); `dragging`/`hoverIndex` mirror it for rendering.
  const drag = useRef({ pressing: false, dragging: false, pointerId: -1, startX: 0 });
  const [dragging, setDragging] = useState(false);
  const [hoverIndex, setHoverIndex] = useState(activeIndex);
  const suppressTap = useRef(false);
  const firstSettle = useRef(true);

  /** Left offset (in the list's padding box) and width of tab `index`. */
  const measure = useCallback((index: number): { left: number; width: number } | null => {
    const el = tabRefs.current[index];
    const list = listRef.current;
    if (!el || !list) return null;
    const er = el.getBoundingClientRect();
    const lr = list.getBoundingClientRect();
    return { left: er.left - lr.left - list.clientLeft, width: er.width };
  }, []);

  const settle = useCallback(
    (index: number, withAnimation: boolean) => {
      const m = measure(index);
      if (!m) return;
      if (withAnimation && !reduceMotion) {
        animate(lensLeft, m.left, SETTLE_SPRING);
        animate(lensWidth, m.width, SETTLE_SPRING);
      } else {
        lensLeft.set(m.left);
        lensWidth.set(m.width);
      }
    },
    [lensLeft, lensWidth, measure, reduceMotion],
  );

  // Park the lens on the active tab whenever the route or tab set changes, and
  // keep it aligned on resize. Skip while the finger is driving it.
  useLayoutEffect(() => {
    if (drag.current.dragging) return;
    settle(activeIndex, !firstSettle.current);
    firstSettle.current = false;
    setHoverIndex(activeIndex);
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!drag.current.dragging) settle(activeIndex, false);
    });
    ro.observe(list);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, items.length, settle]);

  const nearestIndex = useCallback(
    (clientX: number): number => {
      const list = listRef.current;
      if (!list) return activeIndex;
      const x = clientX - list.getBoundingClientRect().left - list.clientLeft;
      let best = 0;
      let bestDistance = Infinity;
      for (let i = 0; i < items.length; i += 1) {
        const m = measure(i);
        if (!m) continue;
        const distance = Math.abs(m.left + m.width / 2 - x);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    },
    [activeIndex, items.length, measure],
  );

  /**
   * Hand the lens to a new tab mid-drag. The lens glides onto the tab rather than
   * free-tracking the finger, so it is always squarely behind the lit (white) icon.
   */
  const moveLensTo = useCallback((index: number) => {
    if (index === hoverIndex) return;
    setHoverIndex(index);
    settle(index, true);
    if (platform.isNative()) {
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => { /* noop */ });
    }
  }, [hoverIndex, settle]);

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    drag.current = { pressing: true, dragging: false, pointerId: event.pointerId, startX: event.clientX };
    suppressTap.current = false;
  };

  const onPointerMove = (event: React.PointerEvent) => {
    if (!drag.current.pressing || event.pointerId !== drag.current.pointerId) return;
    if (!drag.current.dragging && Math.abs(event.clientX - drag.current.startX) < DRAG_THRESHOLD) return;
    if (!drag.current.dragging) {
      drag.current.dragging = true;
      suppressTap.current = true;
      setDragging(true);
      try {
        listRef.current?.setPointerCapture(event.pointerId);
      } catch { /* capture unsupported */ }
    }
    moveLensTo(nearestIndex(event.clientX));
  };

  const endDrag = (event: React.PointerEvent) => {
    if (!drag.current.pressing || event.pointerId !== drag.current.pointerId) return;
    const wasDragging = drag.current.dragging;
    drag.current.pressing = false;
    drag.current.dragging = false;
    try {
      listRef.current?.releasePointerCapture(event.pointerId);
    } catch { /* noop */ }
    setDragging(false);
    if (wasDragging) {
      const index = nearestIndex(event.clientX);
      const target = items[index];
      settle(index, true);
      if (target) navigate(routeFor(target));
    }
  };

  const onTabTap = (index: number) => {
    // A drag that ends on this tab already navigated; ignore the trailing click.
    if (suppressTap.current) {
      suppressTap.current = false;
      return;
    }
    const target = items[index];
    if (target) navigate(routeFor(target));
  };

  return (
    // Floating, click-through nav; only the capsule captures input. Slides off the
    // bottom on scroll-down and back on scroll-up (see useHideOnScroll in the shell).
    <motion.nav
      initial={false}
      animate={{ y: hidden ? '150%' : '0%' }}
      transition={reduceMotion ? { duration: 0 } : HIDE_SPRING}
      className="pointer-events-none absolute inset-x-0 bottom-0 z-50 flex justify-center pb-[max(0.75rem,calc(env(safe-area-inset-bottom)+0.25rem))]">
      <ul
        ref={listRef}
        role="tablist"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={cn(
          'pointer-events-auto relative flex touch-none select-none items-center gap-2 rounded-full px-3 py-2',
          // Liquid glass: translucent, blurred, floating, with a specular top edge.
          'frosted-surface border border-white/40',
          'shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_12px_30px_-12px_rgba(0,0,0,0.45)]',
        )}
      >
        {/* The glass lens that marks / drags the active tab. Hidden behind the panic badge. */}
        <motion.span
          aria-hidden
          style={{ x: lensLeft, width: lensWidth }}
          animate={{ opacity: isPanicTab(items[dragging ? hoverIndex : activeIndex]) ? 0 : 1 }}
          transition={{ duration: 0.18 }}
          className={cn(
            'pointer-events-none absolute left-0 top-2 bottom-2 z-0 rounded-full',
            '[background:color-mix(in_oklch,var(--primary-600)_92%,transparent)]',
            'shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_6px_16px_-6px_color-mix(in_oklch,var(--primary-700)_60%,transparent)]',
          )}
        />
        {items.map((item, index) => {
          const active = index === activeIndex;
          const hovered = dragging && index === hoverIndex;
          // Exactly the tab the lens is parked on lights up: while dragging that is the
          // hovered tab, never the tab the lens has left behind.
          const lit = dragging ? hovered : active;
          return (
            <li key={(item as CompiledNavigationItem).id ?? item.system_key}>
              <button
                type="button"
                ref={(el) => (tabRefs.current[index] = el)}
                role="tab"
                aria-selected={active}
                aria-label={labelFor(item)}
                onClick={() => onTabTap(index)}
                className="relative z-10 flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              >
                {isPanicTab(item) ? (
                  <span className="flex h-12 w-12 items-center justify-center">
                    <PanicSosBadge
                      className={cn(
                        'h-11 w-11 text-[0.7rem] transition-all duration-fast ease-smooth',
                        lit && 'scale-105 ring-2 ring-primary-500 ring-offset-2 ring-offset-card',
                      )}
                    />
                  </span>
                ) : (
                  <span
                    className={cn(
                      'flex h-12 w-12 items-center justify-center transition-[color,transform] duration-fast ease-smooth',
                      lit ? 'text-white' : 'text-primary-600',
                      hovered && 'scale-110',
                    )}
                  >
                    <TabIcon item={item} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </motion.nav>
  );
}

const TAB_ICON_BY_SYSTEM_KEY: Record<string, typeof Home> = {
  home: Home,
  visitors: KeyRound,
  book: CalendarRange,
  estate: LayoutGrid,
  more: LayoutGrid,
  me: CircleUser,
};

function TabIcon({ item }: { item: TabItem }): JSX.Element {
  if ('icon' in item) {
    const Icon = item.icon;
    return <Icon className="h-5 w-5" strokeWidth={1.75} />;
  }

  const systemKey = (item.system_key ?? '').trim().toLowerCase();
  const LucideIcon = TAB_ICON_BY_SYSTEM_KEY[systemKey];
  const iconUrl = item.icon_url?.trim();

  if (systemKey === 'home') {
    return <Home className="h-5 w-5" strokeWidth={1.75} />;
  }
  if (iconUrl) {
    // Server icons are tinted via CSS mask so they inherit the tab's text colour
    // (white when active, brand when inactive) rather than their baked-in colour.
    return (
      <span
        aria-hidden
        className="h-5 w-5 bg-current"
        style={{
          maskImage: `url("${iconUrl}")`,
          WebkitMaskImage: `url("${iconUrl}")`,
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
        }}
      />
    );
  }
  if (LucideIcon) {
    return <LucideIcon className="h-5 w-5" strokeWidth={1.75} />;
  }
  return <MoreHorizontal className="h-5 w-5" strokeWidth={1.75} />;
}
